/**
 * The console's light and dark palettes, and who decides between them.
 *
 * The whole contract is two facts about the document: `data-theme` on the root
 * element is `"light"` or `"dark"` when a person has chosen one, and is absent
 * when the machine's own preference decides. `tokens.css` reads nothing else —
 * every colour is written once as `light-dark(day, night)` and follows
 * `color-scheme`, which those two values set. This module is the only place
 * that writes the attribute or the remembered choice; the small script in
 * `index.html` applies the same rule before the first paint, so the page never
 * flashes the wrong palette on its way in.
 */

import { useCallback, useEffect, useState } from "react";

/** Follow the machine, or overrule it in one direction or the other. */
export type ThemePreference = "system" | "light" | "dark";

/** Where the chosen theme is remembered, between visits and between tabs. */
export const THEME_STORAGE_KEY = "gpu-console-theme";

export interface ThemeControl {
  /** What the console is following right now. */
  preference: ThemePreference;
  /** Choose a theme: it applies at once, and is remembered. */
  setPreference: (next: ThemePreference) => void;
}

/**
 * Read the theme this browser chose last time.
 *
 * Nothing here is worth failing over: a browser that refuses storage
 * (private window, storage turned off) and a value this console never wrote
 * both mean the same thing — nobody has overruled the machine.
 *
 * Returns:
 *     The remembered preference, or `"system"` when there is none to read.
 */
export function readStoredTheme(): ThemePreference {
  let stored: string | null;
  try {
    stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  } catch {
    return "system";
  }
  return stored === "light" || stored === "dark" ? stored : "system";
}

/**
 * Put a preference on the document, where the stylesheet can see it.
 *
 * Args:
 *     preference: The palette to show. `"system"` removes the attribute,
 *         which is how the page hands the decision back to the machine.
 */
export function applyTheme(preference: ThemePreference): void {
  const root = document.documentElement;
  if (preference === "system") {
    root.removeAttribute("data-theme");
    return;
  }
  root.setAttribute("data-theme", preference);
}

/**
 * Remember a preference, or forget it when the machine decides again.
 *
 * Args:
 *     preference: The choice to keep.
 */
function rememberTheme(preference: ThemePreference): void {
  try {
    if (preference === "system") {
      window.localStorage.removeItem(THEME_STORAGE_KEY);
    } else {
      window.localStorage.setItem(THEME_STORAGE_KEY, preference);
    }
  } catch {
    // A browser that will not store this is not a broken console: the choice
    // holds until a reload or the sign-in gate rebuilds the page, and then
    // the machine decides again. It is a preference, not a credential and
    // not data.
  }
}

/**
 * Read and change the palette.
 *
 * Returns:
 *     The current preference and the setter that applies and remembers it.
 */
export function useTheme(): ThemeControl {
  const [preference, setPreferenceState] =
    useState<ThemePreference>(readStoredTheme);

  // The script in `index.html` is what keeps the first paint honest; this puts
  // the attribute back should that script ever not run. It writes a value the
  // document almost always already carries, exactly once.
  useEffect(() => {
    applyTheme(readStoredTheme());
  }, []);

  const setPreference = useCallback((next: ThemePreference): void => {
    setPreferenceState(next);
    applyTheme(next);
    rememberTheme(next);
  }, []);

  return { preference, setPreference };
}
